import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Network, Table2 } from 'lucide-react';
import { Select } from '../ui';
import GraphPanel from '../ide/graph/GraphPanel';
import { GRAPH_MODES } from './constants';
import GraphTable from './GraphTable';
import { fileSelection, nodeSelection, symbolSelection } from './selection';

/**
 * `GraphPanel` reports `{type:'file'|'symbol'|'explore', path?, name?, symbol?}`
 * while `GraphTable` reports a React Flow node. Both are normalised here so
 * the rest of the explorer only ever sees a `KnowledgeSelection`.
 */
function fromGraphPanelNode(nodeData) {
  if (!nodeData) return null;
  if (nodeData.type === 'file' && nodeData.path) {
    return fileSelection({ path: nodeData.path, name: nodeData.name });
  }
  if (nodeData.type === 'symbol') {
    return symbolSelection(nodeData.symbol || { id: nodeData.id, name: nodeData.name });
  }
  return nodeSelection({ id: nodeData.path || nodeData.name, data: { label: nodeData.name, path: nodeData.path } });
}

/**
 * Hosts one graph and its mandatory tabular alternative behind a visible
 * toggle (spec §9.5, §12) — never a hidden preference.
 *
 * The visual graph is the existing `GraphPanel` subsystem, embedded unchanged;
 * the table is `GraphTable`, fed from the same `useGraphData`/transformer
 * output so both views always agree on the numbers.
 *
 * @param {object} props
 * @param {'architecture'|'callgraph'} props.vizType
 * @param {import('./selection').KnowledgeSelection|null} props.selection
 * @param {'graph'|'table'} props.mode
 * @param {(mode: string) => void} props.onModeChange
 * @param {'structure'|'dependencies'} props.archMode - table-side arch mode.
 * @param {(mode: string) => void} props.onArchModeChange
 * @param {(selection: import('./selection').KnowledgeSelection) => void} props.onNodeSelect -
 *   receives an already-normalised selection from either representation.
 * @param {(target: string) => void} props.onImpactAnalysis
 */
export function GraphSurface({
  vizType,
  selection,
  mode,
  onModeChange,
  archMode,
  onArchModeChange,
  onNodeSelect,
  onImpactAnalysis
}) {
  const { t } = useTranslation();

  // GraphPanel expects the pre-Etch `{type:'symbol', symbol:{id}}` shape.
  const graphSelection = useMemo(() => {
    if (selection?.kind !== 'symbol') return null;
    return { type: 'symbol', symbol: { id: selection.id, name: selection.name } };
  }, [selection]);

  const targetSymbol = selection?.kind === 'symbol' ? selection.id : null;
  const isTable = mode === GRAPH_MODES.table;

  return (
    <div className="etch-knowledge__graph" data-mode={mode}>
      <div className="etch-knowledge__graph-toolbar">
        <div className="etch-knowledge__view-toggle" role="group" aria-label={t('knowledge.viewAs')}>
          <button
            type="button"
            className="etch-knowledge__view-option"
            aria-pressed={!isTable}
            onClick={() => onModeChange(GRAPH_MODES.graph)}
          >
            <Network size={15} aria-hidden="true" />
            {t('knowledge.viewGraph')}
          </button>
          <button
            type="button"
            className="etch-knowledge__view-option"
            aria-pressed={isTable}
            onClick={() => onModeChange(GRAPH_MODES.table)}
          >
            <Table2 size={15} aria-hidden="true" />
            {t('knowledge.tableAlternative')}
          </button>
        </div>

        {isTable && vizType === 'architecture' && (
          <Select
            label={t('knowledge.archMode')}
            hideLabel
            value={archMode}
            onChange={(event) => onArchModeChange(event.target.value)}
            options={[
              { value: 'structure', label: t('knowledge.archModeStructure') },
              { value: 'dependencies', label: t('knowledge.archModeDependencies') }
            ]}
            className="etch-knowledge__arch-mode"
          />
        )}
      </div>

      <div className="etch-knowledge__graph-body">
        {isTable ? (
          <GraphTable
            vizType={vizType}
            targetSymbol={targetSymbol}
            archMode={archMode}
            selectedId={selection?.kind === 'node' ? selection.id : null}
            onSelect={(node) => onNodeSelect?.(nodeSelection(node))}
          />
        ) : (
          <GraphPanel
            selection={graphSelection}
            initialVizType={vizType}
            onNodeSelect={(nodeData) => {
              const normalised = fromGraphPanelNode(nodeData);
              if (normalised) onNodeSelect?.(normalised);
            }}
            onImpactAnalysis={onImpactAnalysis}
          />
        )}
      </div>
    </div>
  );
}

export default GraphSurface;
